import { useEffect, useRef, useState } from "react"
import { loadArtworkImageJpeg } from "@services/pdf/assetLoader"
import { previewRefreshMs } from "@services/pdf/constants"
import { generatePage, IPdfStrings } from "@services/pdf/generator"
import { pdfPageToDataUrl } from "@services/pdf/rasterize"
import { IPdfImageUrl, IPdfOptions, TPdfPageType } from "@services/pdf/types"

// module-level cache so switching steps or re-mounting rows is instant
const thumbnailCache = new Map<string, Promise<string | null>>()

// image side cap for thumbnail source images (iOS resizes for previews)
const thumbImageMaxSide = 600

// CSS width the thumbnails are rasterized at (desktop card size)
const thumbRenderWidth = 110

function typeKey(type: TPdfPageType): string {
	switch (type.kind) {
		case "cover":
			return `cover|${type.layoutType}|${type.templateBytes ? 1 : 0}`
		case "wcCover":
			return `wcCover|${type.templateBytes ? 1 : 0}`
		case "wcImageCover":
			return `wcImageCover|${type.templateBytes ? 1 : 0}`
		case "image":
			return `image|${type.fill ? "fill" : "fit"}`
		case "imageInfo":
			return `imageInfo|${type.layoutType}|${type.item.id}|${type.templateBytes ? 1 : 0}`
		case "wcImageInfo":
			return `wcImageInfo|${type.item.id}|${type.templateBytes ? 1 : 0}`
		case "attachment":
			return `attachment|${type.attachment.id}`
	}
}

async function renderThumbnail(
	type: TPdfPageType,
	imageUrl: IPdfImageUrl | null,
	customInput: string,
	options: IPdfOptions,
	strings: IPdfStrings
): Promise<string | null> {
	try {
		// attachment rows show the Airtable-provided thumbnail image directly
		if (type.kind === "attachment") {
			return type.attachment.pdfImagePath || null
		}
		const imageJpeg = imageUrl ? await loadArtworkImageJpeg(imageUrl, thumbImageMaxSide) : null
		const bytes = await generatePage(type, { imageJpeg }, options, customInput, strings)
		if (!bytes) {
			return null
		}
		return await pdfPageToDataUrl(bytes, 0, thumbRenderWidth)
	} catch {
		return null
	}
}

/**
 * Live page thumbnail: real generator output rasterized small (iOS renders
 * actual previews at 15%). Re-renders previewRefreshMs after inputs settle
 * (iOS refreshes 0.5s after typing stops).
 */
export function usePageThumbnail(
	type: TPdfPageType,
	imageUrl: IPdfImageUrl | null,
	customInput: string,
	options: IPdfOptions,
	strings: IPdfStrings
): { dataUrl: string | null; loading: boolean } {
	const [dataUrl, setDataUrl] = useState<string | null>(null)
	const [loading, setLoading] = useState(true)
	const firstRender = useRef(true)

	const optionsKey = `${options.activeOptions.slice().sort().join(",")}|${options.hasBrand ? 1 : 0}`
	const cacheKey = `${typeKey(type)}|${imageUrl?.url ?? ""}|${customInput}|${optionsKey}`

	useEffect(() => {
		let cancelled = false

		const render = () => {
			let promise = thumbnailCache.get(cacheKey)
			if (!promise) {
				promise = renderThumbnail(type, imageUrl, customInput, options, strings)
				thumbnailCache.set(cacheKey, promise)
				promise.then((result) => {
					if (result === null) {
						thumbnailCache.delete(cacheKey)
					}
				})
			}
			promise.then((result) => {
				if (!cancelled) {
					setDataUrl(result)
					setLoading(false)
				}
			})
		}

		// immediate on first render, debounced while typing (iOS 0.5s timer)
		if (firstRender.current) {
			firstRender.current = false
			render()
			return () => {
				cancelled = true
			}
		}

		setLoading(true)
		const timer = window.setTimeout(render, previewRefreshMs)
		return () => {
			cancelled = true
			window.clearTimeout(timer)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cacheKey])

	return { dataUrl, loading }
}
